/**
 * Which named sections an operator has folded away.
 *
 * The cockpit's rail carries four answers at once — the session's machine, who
 * is waiting, open risks, pool quotas — and which of them matters depends on
 * what the operator is doing. Folding is how they say so, and a fold that does
 * not survive a reload is a preference the console keeps asking about.
 *
 * **Only an explicit choice is stored.** The map holds what the operator
 * actually pressed, never the default, so a section added later arrives *open*
 * rather than silently hidden behind a stale record of a screen that did not
 * have it. That is the same rule the wall's prune follows: a stored key is a
 * claim about something the operator did, not a description of the screen.
 *
 * Local rather than server-side, for the reason the wall's layouts are:
 * `/system/settings` has no per-operator scope, so a fold stored there would be
 * one person's rail imposed on everyone.
 */

import { useCallback, useMemo, useState } from 'react'

export interface Disclosures {
  /** Whether a section is open — its default unless the operator changed it. */
  isOpen: (id: string) => boolean
  /** Flips one section and remembers that it was flipped. */
  toggle: (id: string) => void
}

function read(key: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return {}
    const v: unknown = JSON.parse(raw)
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {}
    const out: Record<string, boolean> = {}
    for (const [k, on] of Object.entries(v as Record<string, unknown>)) {
      if (typeof on === 'boolean') out[k] = on
    }
    return out
  } catch {
    // Unreadable is not an error to report. It is a preference, and the default
    // is a correct answer.
    return {}
  }
}

/**
 * @param key where the choices are kept, one key per rail.
 * @param closedByDefault sections that start folded. Keep attention out of it —
 *   a section nobody has opened yet should not be the one hiding a stopped
 *   agent.
 */
export function useDisclosures(key: string, closedByDefault: readonly string[] = []): Disclosures {
  const [chosen, setChosen] = useState<Record<string, boolean>>(() => read(key))

  // Serialised before it reaches a dependency list, never passed by reference: a
  // caller's inline array is a new value every render, and this codebase has
  // already shipped one hook that re-ran itself to a standstill over exactly
  // that (useSequence, which cancelled its own timer every state change).
  const closedKey = closedByDefault.join(',')
  const closed = useMemo(
    () => new Set(closedKey ? closedKey.split(',') : []),
    [closedKey],
  )

  const isOpen = useCallback((id: string) => chosen[id] ?? !closed.has(id), [chosen, closed])

  const toggle = useCallback(
    (id: string) => {
      setChosen((prev) => {
        const next = { ...prev, [id]: !(prev[id] ?? !closed.has(id)) }
        try {
          localStorage.setItem(key, JSON.stringify(next))
        } catch {
          // A full or blocked store must not take the rail down with it. The
          // fold stays correct for this tab; it just will not outlive it.
        }
        return next
      })
    },
    [key, closed],
  )

  return { isOpen, toggle }
}
